<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useADialog, useAToast } from '@Atoms';
import { ADOPT_URL, getShelterNow } from '@Constants';
import { getPayloadDateTimeString } from '@Helpers';
import { useSessionStore } from '@Stores';
import { GetStartFosterReasonsService, StartFosterService } from '@AnimalProfileModule';
import { getFosterContractsService } from '@FosterModule';
import {
  CART_STEPS,
  CompleteFosterManualCheckoutService,
  DeleteCartSessionService,
  fosterCartLinePetId,
  GetWaiversService,
  useCartSteps,
} from '@ShoppingCartModule';
import { DeleteCartItem, SendCheckoutLinkService } from '../../../services';
import useCartStore from '../../../store';

const emit = defineEmits<{
  onStepUpdate: [stepName: CART_STEPS];
}>();

const cartStore = useCartStore();
const { cartData, cartId, loading: cartLoading, pendingOutcomeRoute, people } = storeToRefs(cartStore);
const { outcomeCompletedCartReset, resetCart } = cartStore;
const { shelterProfile } = storeToRefs(useSessionStore());

const { activeDialog, activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { notify, TOAST_TYPES } = useAToast();
const { currentFlow } = useCartSteps();
const router = useRouter();

const hasEquipmentRentalStepNext = computed(() => currentFlow.value.includes(CART_STEPS.FOSTER_EQUIPMENT_RENTAL));

const fosterStartDate = ref<Date>(cartStore.fosterCheckoutShared.fosterStartDate ?? getShelterNow());
const fosterContractId = ref<string | null>(cartStore.fosterCheckoutShared.fosterContractId ?? null);
const animalOverrides = ref<Record<string, { waiver_id?: string; waiver_ids?: string[]; notes?: string }>>({
  ...cartStore.fosterCheckoutShared.animalOverrides,
});

const fosterContractOptions = ref<any[]>([]);
const startFosterReasons = ref<any[]>([]);
const waiversOptions = ref<any[]>([]);
const fosterStartDateError = ref(false);
const formTouched = ref(false);
const fosterStartReasonErrors = ref<Record<string, boolean>>({});
const completeCheckoutLoading = ref(false);
const fosterContractsLoading = ref(true);
const mobileCheckoutModalVisible = ref(false);
const mobileLinkLoading = ref(false);

const startFosterReasonRequired = computed(() => !!shelterProfile.value?.foster_start_reason_required);
const canReserveKennelSpot = computed(() => !!shelterProfile.value?.use_kennel_manager);
const fosterContractsEnabled = computed(() => !!shelterProfile.value?.foster_waivers_enabled);
const fosterWaiversEnabled = computed(
  () => fosterContractsEnabled.value && !!shelterProfile.value?.enable_outcome_waivers
);
const animals = computed(() => cartData.value?.animals ?? []);
const hasFosterContractOptions = computed(() => fosterContractOptions.value.length > 0);
const showFosterContractNoOptionsHint = computed(
  () =>
    fosterContractsEnabled.value &&
    !cartLoading.value &&
    !fosterContractsLoading.value &&
    !hasFosterContractOptions.value
);
const adopterAppLink = computed(() => ADOPT_URL + '/mobile-checkout/' + cartId.value);
const fosterContractError = computed(
  () => formTouched.value && fosterContractsEnabled.value && !fosterContractId.value
);
const canRemoveFosterAnimalRow = computed(() => animals.value.length > 1);
const waiverOptionsWithId = computed(() =>
  waiversOptions.value
    .map((waiver) => ({
      ...waiver,
      id: String(waiver._id?.$oid ?? waiver._id ?? ''),
      title: waiver.title ?? waiver.name ?? waiver.label,
    }))
    .filter((waiverOption) => waiverOption.id !== '')
);

onMounted(() => initFosterCheckoutStep());

watch(mobileCheckoutModalVisible, changeMobileCheckoutModalVisibility);

watch(fosterStartDate, syncSharedFosterStartDate, { immediate: true });
watch(fosterContractId, syncSharedFosterContractId, { immediate: true });
watch(animalOverrides, syncSharedAnimalOverrides, { deep: true, immediate: true });

// HELPERS
function syncSharedFosterStartDate(next: Date) {
  cartStore.fosterCheckoutShared.fosterStartDate = next;
}

function syncSharedFosterContractId(next: string | null) {
  cartStore.fosterCheckoutShared.fosterContractId = next;
}

function syncSharedAnimalOverrides(
  next: Record<string, { waiver_id?: string; waiver_ids?: string[]; notes?: string }>
) {
  cartStore.fosterCheckoutShared.animalOverrides = { ...next };
}

function validateForm(): boolean {
  formTouched.value = true;
  fosterStartDateError.value = !fosterStartDate.value;

  const nextReasonErrors: Record<string, boolean> = {};
  if (startFosterReasonRequired.value) {
    for (const animal of animals.value) {
      const key = fosterCartLinePetId(animal);
      if (key === '') continue;
      if (!(animal.start_foster_reason_id?.$oid || animal.start_foster_reason_id)) nextReasonErrors[key] = true;
    }
  }
  fosterStartReasonErrors.value = nextReasonErrors;

  return !fosterStartDateError.value && !fosterContractError.value && !Object.keys(nextReasonErrors).length;
}

function collectWaiverIdsFromListForPayload(list: unknown): string[] {
  if (!Array.isArray(list)) return [];

  const ids: string[] = [];
  for (const waiverEntry of list) {
    if (waiverEntry == null) continue;
    if (typeof waiverEntry === 'string') {
      ids.push(waiverEntry);
      continue;
    }
    const oid = waiverEntry?._id?.$oid ?? waiverEntry?._id ?? waiverEntry?.id?.$oid ?? waiverEntry?.id;
    if (oid != null && oid !== '') ids.push(String(oid));
  }
  return ids;
}

function getWaiverIdsForStartFoster(animal: any): string[] | undefined {
  if (!fosterWaiversEnabled.value) return undefined;

  const allowedWaiverIds = new Set(waiverOptionsWithId.value.map((waiverOption) => waiverOption.id));
  const id = fosterCartLinePetId(animal);
  const override = id !== '' ? animalOverrides.value[id] : undefined;
  if (override != null && Object.prototype.hasOwnProperty.call(override, 'waiver_ids')) {
    const filteredOverrideWaiverIds = (override.waiver_ids ?? []).filter((waiverId) =>
      allowedWaiverIds.has(String(waiverId))
    );
    return filteredOverrideWaiverIds.length ? filteredOverrideWaiverIds : undefined;
  }
  if (override?.waiver_id && allowedWaiverIds.has(String(override.waiver_id))) return [override.waiver_id];

  const fromAnimal = [
    ...new Set([
      ...collectWaiverIdsFromListForPayload(animal.intake_waivers),
      ...collectWaiverIdsFromListForPayload(animal.waivers),
    ]),
  ].filter((waiverId) => allowedWaiverIds.has(String(waiverId)));

  if (fromAnimal.length) return fromAnimal;
  return undefined;
}

function getFosterAnimalsWaiversRowForPayload(animal: any) {
  const id = fosterCartLinePetId(animal);
  const rawIds = getWaiverIdsForStartFoster(animal);
  const notesText = id !== '' ? (animalOverrides.value[id]?.notes ?? '') : '';
  const reasonId = animal.start_foster_reason_id?.$oid || animal.start_foster_reason_id;

  return {
    animal_id: id,
    waiver_ids: Array.isArray(rawIds) ? rawIds.map((waiverId) => String(waiverId)) : [],
    reserve_kennel_spot: !!animal.reserve_kennel_spot,
    start_date: getPayloadDateTimeString(fosterStartDate.value) ?? '',
    ...(notesText ? { start_foster_notes: notesText } : {}),
    ...(animal.start_foster_reason ? { start_foster_reason: animal.start_foster_reason } : {}),
    ...(reasonId ? { start_foster_reason_id: String(reasonId) } : {}),
  };
}

function buildStartFosterPayload(
  animal: any,
  startDateStr: string,
  person: any,
  PAYLOAD?: { cart_id: string; contract_id: string }
) {
  const id = fosterCartLinePetId(animal);
  const notesText = id !== '' ? (animalOverrides.value[id]?.notes ?? '') : '';
  const reasonId = animal.start_foster_reason_id?.$oid || animal.start_foster_reason_id;
  const waiverIdsRaw = getWaiverIdsForStartFoster(animal);

  return {
    intake_id: animal.intake_id?.$oid || animal.intake_id,
    parent_id: animal.parent_id?.$oid || animal.parent_id || person?._id?.$oid || person?._id || '',
    parent_name: animal.parent_name || person?.full_name || '',
    pet_id: id || animal.animal_id?.$oid || animal.animal_id,
    start_date: startDateStr,
    reserve_kennel_spot: !!animal.reserve_kennel_spot,
    ...(notesText ? { start_foster_notes: notesText } : {}),
    ...(animal.start_foster_reason ? { start_foster_reason: animal.start_foster_reason } : {}),
    ...(reasonId ? { start_foster_reason_id: reasonId } : {}),
    ...(waiverIdsRaw?.length ? { waiver_ids: waiverIdsRaw } : {}),
    ...(PAYLOAD
      ? {
          foster_manual_checkout_cart_id: PAYLOAD.cart_id,
          foster_manual_checkout_contract_id: PAYLOAD.contract_id,
        }
      : {}),
  } as any;
}

function changeMobileCheckoutModalVisibility(isOpen: boolean) {
  if (isOpen) return;

  mobileLinkLoading.value = false;
}

// SERVICES
function runStartFosterForAllAnimals(PAYLOAD?: { cart_id: string; contract_id: string }) {
  const startDateStr = getPayloadDateTimeString(fosterStartDate.value) ?? '';
  const person = Array.isArray(cartData.value?.people) ? cartData.value?.people[0] : cartData.value?.people;

  return animals.value.reduce(
    (promiseChain: Promise<unknown>, animal: any) =>
      promiseChain.then(() => StartFosterService(buildStartFosterPayload(animal, startDateStr, person, PAYLOAD))),
    Promise.resolve() as Promise<unknown>
  );
}

function startFoster() {
  const TOAST_SUCCESS = {
    title: 'cart.toast.fosterCheckoutComplete.title',
    message: 'cart.toast.fosterCheckoutComplete.message',
    type: TOAST_TYPES.SUCCESS,
  };

  return runStartFosterForAllAnimals()
    .then(() => DeleteCartSessionService(false))
    .then(() => {
      resetCart();
      notify(TOAST_SUCCESS);
      return router.push({ name: 'animals-in-foster' });
    });
}

async function initFosterCheckoutStep() {
  const reasonsResponse = await GetStartFosterReasonsService();
  const reasonsList = Array.isArray(reasonsResponse) ? reasonsResponse : (reasonsResponse as any)?.data;
  startFosterReasons.value = Array.isArray(reasonsList) ? reasonsList : [];

  if (fosterContractsEnabled.value && !waiversOptions.value.length) {
    const waiversRes = await GetWaiversService('outcome', 'foster_checkout');
    waiversOptions.value = waiversRes?.waivers ?? (Array.isArray(waiversRes) ? waiversRes : []);
  }

  // Foster Contract & Waivers off → the shelter doesn't want a contract attached to this checkout.
  // Skip the fetch entirely and clear any leftover selection so downstream steps (equipment rental
  // manual checkout, payment, …) ship `foster_contract_id: ''` and the cart never stores one. Without
  // this, the auto-pick below would adopt the shelter's `default_foster_contract_id` (or the first
  // available template) even when staff can't see/select a contract — and the completion view's
  // print menu would surface the resulting "Foster Contract" + "All Documents" options.
  if (!fosterContractsEnabled.value) {
    fosterContractOptions.value = [];
    fosterContractId.value = null;
    fosterContractsLoading.value = false;
    return;
  }

  return getFosterContractsService()
    .then((raw) => {
      const contracts = Array.isArray(raw) ? raw : [];
      fosterContractOptions.value = contracts
        .map((contract: any) => ({
          label: contract.title ?? contract.name ?? '',
          value: contract._id?.$oid ?? contract._id ?? '',
        }))
        .filter((contractOption: { value: string }) => contractOption.value !== '');

      const alreadyHasValidSelection =
        !!fosterContractId.value &&
        fosterContractOptions.value.some((option) => option.value === fosterContractId.value);
      if (alreadyHasValidSelection) return;

      const defaultContract: any = shelterProfile.value?.default_foster_contract_id;
      if (!defaultContract) {
        fosterContractId.value = null;
        return;
      }

      const defaultOid = defaultContract?.$oid ?? defaultContract ?? '';
      const match = contracts.find((contract: any) => contract._id?.$oid ?? contract._id ?? '' === defaultOid);
      const firstId = contracts[0] ? (contracts[0]._id?.$oid ?? contracts[0]._id ?? '') : '';
      fosterContractId.value = match ? defaultOid : firstId || null;
    })
    .catch(() => {
      fosterContractOptions.value = [];
      // NOTE: Don't wipe a previously selected contract on a transient fetch error — keep the user's pick.
    })
    .finally(() => (fosterContractsLoading.value = false));
}

// EVENTS
function onAdvanceToEquipmentRentalStep() {
  if (!validateForm()) return;

  emit('onStepUpdate', CART_STEPS.FOSTER_EQUIPMENT_RENTAL);
}

function onCancelFoster() {
  activeDialog.value = SYSTEM_MODALS.CART_EMPTY_DIALOG;
  dialogVisibility.value = true;
}

function onRemoveFosterAnimalRequest(animal: any, index: number) {
  activeModal.value = SYSTEM_MODALS.CART_REMOVE_ANIMAL;
  activeModalProps.value = {
    animal,
    index,
    isLastAnimal: false,
    onConfirm: () => onRemoveFosterAnimalConfirmed(index),
  };
  dialogVisibility.value = true;
}

function onContinueMobileCheckout() {
  if (!validateForm()) return;
  mobileCheckoutModalVisible.value = true;
}

function onFosterMobileCheckoutDialogSubmit(detail: {
  checkoutFlow: string;
  deviceCheckoutMethod: string;
  email: string;
  phone: string;
}) {
  if (mobileLinkLoading.value) return;

  mobileLinkLoading.value = true;

  let email = detail.email;
  const phone = detail.checkoutFlow === 'adopter' ? detail.phone : '';

  if (
    detail.checkoutFlow !== 'adopter' &&
    !(detail.checkoutFlow === 'shelter' && detail.deviceCheckoutMethod === 'link')
  ) {
    email = '';
  }

  const PAYLOAD: Record<string, unknown> = {
    _id: cartId.value,
    cart_id: cartId.value,
    cart_type: 'foster',
    checkoutFlow: detail.checkoutFlow,
    email,
    payment_type: null,
    phone,
    show_application: null,
    show_contract: null,
    status: 'pending',
  };

  if (fosterContractsEnabled.value && fosterContractId.value) {
    PAYLOAD.foster_contract_id = String(fosterContractId.value);
  }

  if (cartData.value?.cart_type === 'foster') {
    PAYLOAD.foster_animals_waivers = animals.value.map(getFosterAnimalsWaiversRowForPayload);
  }

  const TOAST_ERROR = {
    message: 'errors.generics.unknownIssue.message',
    title: 'errors.generics.unknownIssue.title',
    type: TOAST_TYPES.ERROR,
  };

  SendCheckoutLinkService(PAYLOAD as any)
    .then(() => {
      if (detail.deviceCheckoutMethod === 'newTab') window.open(adopterAppLink.value, '_blank');
      mobileCheckoutModalVisible.value = false;

      return router.push(pendingOutcomeRoute.value!).then(() => outcomeCompletedCartReset());
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (mobileLinkLoading.value = false));
}

function onRemoveFosterAnimalConfirmed(index: number) {
  cartLoading.value = true;
  const TOAST_SUCCESS = {
    title: 'cart.toast.removeAnimal.success.title',
    type: TOAST_TYPES.INFO,
  };

  return DeleteCartItem(index)
    .then(({ data }: any) => notify(TOAST_SUCCESS) && (cartData.value = data))
    .finally(() => (cartLoading.value = false));
}

function onCompleteFosterCheckout() {
  if (completeCheckoutLoading.value) return;

  if (!validateForm()) return;

  const TOAST_ERROR = {
    title: 'errors.generics.unknownIssue.title',
    message: 'errors.generics.unknownIssue.message',
    type: TOAST_TYPES.ERROR,
  };

  completeCheckoutLoading.value = true;

  // NOTE :: Reset the lock only on error; on success we navigate away, and resetting it before the
  // async router push finishes would re-enable the button and allow a duplicate completion.
  startFoster().catch(() => notify(TOAST_ERROR) && (completeCheckoutLoading.value = false));
}

function onManualFosterCheckout() {
  if (completeCheckoutLoading.value) return;

  if (!validateForm()) return;

  const rawCartId = cartData.value?.cart_id?.$oid || cartData.value?.cart_id;
  const contractId = fosterContractId.value;
  if (!rawCartId || !contractId) return;

  const TOAST_ERROR = {
    title: 'errors.generics.unknownIssue.title',
    message: 'errors.generics.unknownIssue.message',
    type: TOAST_TYPES.ERROR,
  };

  const TOAST_SUCCESS = {
    title: 'cart.toast.fosterCheckoutComplete.title',
    message: 'cart.toast.fosterCheckoutComplete.message',
    type: TOAST_TYPES.SUCCESS,
  };

  completeCheckoutLoading.value = true;

  const PAYLOAD = {
    cart_id: String(rawCartId),
    contract_id: String(contractId),
  };

  runStartFosterForAllAnimals(PAYLOAD)
    .then(() =>
      CompleteFosterManualCheckoutService({
        foster_contract_id: contractId,
        foster_animals_waivers: animals.value.map(getFosterAnimalsWaiversRowForPayload),
        ...cartData.value,
      })
    )
    .then(() => {
      resetCart();
      notify(TOAST_SUCCESS);
      return router.push({ name: 'foster-payment-completed', params: { id: rawCartId } });
    })
    // NOTE :: Reset the lock only on error; on success we navigate away.
    .catch(() => notify(TOAST_ERROR) && (completeCheckoutLoading.value = false));
}
</script>

<template>
  <div class="flex max-w-[1440px] flex-col gap-6">
    <div class="flex flex-wrap items-start gap-6">
      <div class="flex flex-col gap-2">
        <AFieldLabel
          required
          label="cart.label.fosterStartDate"
          :error="formTouched && fosterStartDateError"
        />

        <MFieldDatePicker
          v-model="fosterStartDate"
          showTime
          id="foster-start-date"
          class="w-full min-w-[200px] max-w-[240px]"
          :error="formTouched && fosterStartDateError ? ' ' : undefined"
        />
      </div>

      <div
        v-if="fosterContractsEnabled"
        class="flex flex-col gap-2"
      >
        <AFieldLabel
          required
          label="cart.label.fosterContract"
          :error="fosterContractError"
        />

        <AFieldDropdown
          v-model="fosterContractId"
          dontShowClearTrigger
          id="foster-contract"
          class="w-[240px]"
          optionLabel="label"
          optionValue="value"
          :error="fosterContractError"
          :options="fosterContractOptions"
          :placeholder="$t('cart.label.fosterContract')"
        />

        <p
          v-if="showFosterContractNoOptionsHint"
          class="max-w-[min(100%,320px)] py-1 text-xs leading-snug text-slate-500"
        >
          {{ $t('cart.label.fosterContractNoOptionsHint') }}
        </p>

        <AFieldError
          v-if="fosterContractError"
          error="components.forms.errors.required"
        />
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <h4 class="font-bold text-gray-700">
        {{ $t('cart.label.fosterAnimalDetails') }}
      </h4>

      <MFosterCheckoutAnimalsTable
        v-model:animalOverrides="animalOverrides"
        v-model:fosterStartReasonErrors="fosterStartReasonErrors"
        :animals
        :canRemoveAnimalRows="canRemoveFosterAnimalRow"
        :canReserveKennelSpot
        :formTouched
        :fosterContractsEnabled
        :fosterWaiversEnabled
        :startFosterReasonRequired
        :startFosterReasons
        :waiverOptionsWithId
        @removeAnimal="onRemoveFosterAnimalRequest"
      />
    </div>

    <div class="flex flex-wrap items-center justify-between gap-3 pt-2">
      <Button
        class="af-button w-fit"
        icon="pi pi-trash"
        severity="danger"
        :label="$t('cart.label.cancelFoster')"
        @click="onCancelFoster"
      />

      <Button
        v-if="hasEquipmentRentalStepNext"
        class="af-button af-button-success w-fit"
        icon="pi pi-arrow-right"
        :label="$t('common.nextStep')"
        @click="onAdvanceToEquipmentRentalStep"
      />

      <div
        v-else-if="fosterContractsEnabled"
        class="flex flex-wrap items-center gap-2"
      >
        <Button
          class="af-button af-button-success w-fit"
          icon="pi pi-mobile"
          :label="$t('cart.label.continueMobileCheckout')"
          @click="onContinueMobileCheckout"
        />

        <Button
          class="af-button af-button-success w-fit"
          icon="pi pi-user"
          :label="$t('cart.label.continueManualCheckout')"
          :loading="completeCheckoutLoading"
          @click="onManualFosterCheckout"
        />
      </div>

      <Button
        v-else
        class="af-button af-button-success w-fit"
        icon="pi pi-check"
        :label="$t('cart.label.completeFosterCheckout')"
        :loading="completeCheckoutLoading"
        @click="onCompleteFosterCheckout"
      />
    </div>

    <OFosterMobileCheckoutDialog
      v-model:visible="mobileCheckoutModalVisible"
      :people
      :submitLoading="mobileLinkLoading"
      @onSubmit="onFosterMobileCheckoutDialogSubmit"
    />
  </div>
</template>
